import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    alpha,
    Alert,
    Box,
    Button,
    Container,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getSubjectList } from "../../../redux/sclassRelated/sclassHandle";
import HomeroomAssignSubjectsFields from "../../../components/admin/HomeroomAssignSubjectsFields";
import { HOMEROOM_ASSIGN_SUBJECTS_MSG } from "../../../utils/homeroomAssignSubjects";
import { useHomeroomTeacherTerm } from "../../../hooks/useHomeroomTeacherTerm";
import {
    getHomeroomStaffRoleValue,
    isHomeroomTeacherRole,
    normalizeStaffRole,
} from "../../../utils/staffRoles";

const SetupHomeroomStaff = () => {
    const { classID } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);
    const { subjectsList, subloading } = useSelector((state) => state.sclass);
    const homeroomTerm = useHomeroomTeacherTerm();

    const staffRole = normalizeStaffRole(
        location.state?.staffRole || getHomeroomStaffRoleValue(currentUser?.schoolType),
    );
    const [selectedSubjectIds, setSelectedSubjectIds] = useState(
        () => (Array.isArray(location.state?.homeroomSubjectIds) ? location.state.homeroomSubjectIds : []),
    );
    const [className, setClassName] = useState(location.state?.className || "");

    useEffect(() => {
        if (!isHomeroomTeacherRole(staffRole)) {
            navigate("/Admin/staff/role", { replace: true });
            return;
        }
        if (!classID) {
            navigate("/Admin/teachers/chooseclass", { state: { staffRole }, replace: true });
            return;
        }
        dispatch(getSubjectList(classID, "ClassSubjects"));
    }, [classID, dispatch, navigate, staffRole]);

    useEffect(() => {
        if (location.state?.className) {
            setClassName(location.state.className);
        }
    }, [location.state?.className]);

    const classSubjects = useMemo(
        () => (Array.isArray(subjectsList) ? subjectsList : []),
        [subjectsList],
    );

    const toggleSubject = (subjectId) => {
        setSelectedSubjectIds((prev) => {
            const set = new Set(prev);
            if (set.has(subjectId)) set.delete(subjectId);
            else set.add(subjectId);
            return [...set];
        });
    };

    const continueToCredentials = () => {
        if (!selectedSubjectIds.length) return;
        navigate("/Admin/staff/add", {
            state: {
                staffRole,
                homeroomClassId: classID,
                homeroomClassName: className || "Selected class",
                homeroomSubjectIds: selectedSubjectIds,
            },
        });
    };

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${t.palette.grey[50]} 40%)`
                        : alpha(t.palette.common.black, 0.15),
            }}
        >
            <Container maxWidth="sm">
                <Button
                    startIcon={<ArrowBackIosNewOutlinedIcon />}
                    onClick={() => navigate("/Admin/teachers/chooseclass", { state: { staffRole } })}
                    sx={{ mb: 2 }}
                >
                    Back to classes
                </Button>
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2.5, sm: 3.5 },
                        borderRadius: 3,
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    }}
                >
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                        Subjects for {homeroomTerm.roleTitleCaps}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Choose which subjects they will teach in{" "}
                        <strong>{className || "this class"}</strong>. On the next step you will create their temporary
                        login credentials.
                    </Typography>

                    <HomeroomAssignSubjectsFields
                        homeroomLabel={homeroomTerm.assignOnClass}
                        classId={classID}
                        showClassSelect={false}
                        subjects={classSubjects}
                        subjectsLoading={subloading}
                        selectedSubjectIds={selectedSubjectIds}
                        onToggleSubject={toggleSubject}
                        currentTeacherId=""
                    />

                    <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/Admin/teachers/chooseclass", { state: { staffRole } })}
                        >
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            endIcon={<ChevronRightIcon />}
                            onClick={continueToCredentials}
                            disabled={!selectedSubjectIds.length}
                        >
                            Continue to login setup
                        </Button>
                    </Stack>
                    {!selectedSubjectIds.length && classSubjects.length > 0 ? (
                        <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                            {HOMEROOM_ASSIGN_SUBJECTS_MSG}
                        </Alert>
                    ) : null}
                </Paper>
            </Container>
        </Box>
    );
};

export default SetupHomeroomStaff;
